import { useState } from "react";
import { AiFillStar, AiOutlineStar, AiOutlineClose } from "react-icons/ai";
import { toast } from "react-toastify";

const ReviewForm = ({
	productId,
	productName,
	productImage,
	onClose,
	onReviewSubmit
}) => {
	const [reviewText, setReviewText] = useState("");
	const [rating, setRating] = useState(0);
	const [hoverRating, setHoverRating] = useState(0);
	const [reviewImages, setReviewImages] = useState([]);
	const [imagePreviews, setImagePreviews] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	const handleImageUpload = (e) => {
		const files = Array.from(e.target.files);

		if (files.length > 5) {
			toast.error("You can upload a maximum of 5 images");
			return;
		}

		setReviewImages(files);

		// Create image previews
		const previews = files.map(file => URL.createObjectURL(file));
		setImagePreviews(previews);
	};

	const removeImage = (index) => {
		const newImages = [...reviewImages];
		newImages.splice(index, 1);
		setReviewImages(newImages);

		const newPreviews = [...imagePreviews];
		URL.revokeObjectURL(newPreviews[index]); // Clean up memory
		newPreviews.splice(index, 1);
		setImagePreviews(newPreviews);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (rating === 0) {
			toast.error("Please select a rating");
			return;
		}

		setSubmitting(true);
		try {
			await onReviewSubmit({
				rating,
				reviewText,
				reviewImages
			});
			// Clean up image previews
			imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
			onClose();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
			<div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[90vh] flex flex-col">
				<div className="flex justify-between items-start mb-6">
					<div>
						<h3 className="text-2xl font-bold text-gray-800">Write a Review</h3>
						<p className="text-lg text-gray-600 mt-2">{productName}</p>
					</div>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700 p-1"
					>
						<AiOutlineClose size={28} />
					</button>
				</div>

				<div className="flex flex-col md:flex-row gap-8 overflow-y-auto">
					{/* Product Image Section */}
					<div className="md:w-1/3">
						{productImage && (
							<div className="mb-6">
								<img
									src={productImage}
									alt={productName}
									className="w-full h-auto max-h-64 object-contain rounded-lg"
								/>
							</div>
						)}
					</div>

					{/* Form Section */}
					<div className="md:w-2/3">
						<form onSubmit={handleSubmit}>
							<div className="mb-6">
								<label className="block text-lg font-medium text-gray-700 mb-3">
									Your Rating <span className="text-red-500">*</span>
								</label>
								<div className="flex space-x-2">
									{[1, 2, 3, 4, 5].map((star) => (
										<button
											key={star}
											type="button"
											className="text-3xl focus:outline-none"
											onMouseEnter={() => setHoverRating(star)}
											onMouseLeave={() => setHoverRating(0)}
											onClick={() => setRating(star)}
										>
											{star <= (hoverRating || rating) ? (
												<AiFillStar className="text-yellow-400" />
											) : (
												<AiOutlineStar className="text-yellow-400" />
											)}
										</button>
									))}
								</div>
								<p className="text-sm text-gray-500 mt-2">
									{rating === 0 ? "Select your rating" :
										rating === 1 ? "Poor" :
											rating === 2 ? "Fair" :
												rating === 3 ? "Good" :
													rating === 4 ? "Very Good" : "Excellent"}
								</p>
							</div>

							<div className="mb-6">
								<label
									htmlFor="review-text"
									className="block text-lg font-medium text-gray-700 mb-3"
								>
									Your Review <span className="text-red-500">*</span>
								</label>
								<textarea
									id="review-text"
									rows="6"
									className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
									placeholder="Share your detailed experience with this product..."
									value={reviewText}
									onChange={(e) => setReviewText(e.target.value)}
									required
								></textarea>
							</div>

							<div className="mb-8">
								<label className="block text-lg font-medium text-gray-700 mb-3">
									Upload Images (Optional)
								</label>
								<div className="flex flex-col md:flex-row items-center space-x-4">
									<label className="cursor-pointer">
										<span className="px-6 py-3 bg-blue-200 text-blue-700 rounded-lg text-base font-semibold hover:bg-blue-100 transition-colors">
											Choose Images
										</span>
										<input
											type="file"
											multiple
											accept="image/*"
											onChange={handleImageUpload}
											className="hidden"
										/>
									</label>
									<span className="text-sm text-gray-500 mt-3 md:mt-0">
										{imagePreviews.length}/5 images
									</span>
								</div>
								<p className="mt-2 text-sm text-gray-500">
									JPEG, PNG (max 5MB each)
								</p>

								{/* Image previews */}
								{imagePreviews.length > 0 && (
									<div className="mt-4">
										<div className="flex flex-wrap gap-4">
											{imagePreviews.map((preview, index) => (
												<div key={index} className="relative group">
													<img
														src={preview}
														alt={`Preview ${index}`}
														className="w-24 h-24 object-cover rounded-lg border border-gray-200"
													/>
													<button
														type="button"
														onClick={() => removeImage(index)}
														className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 transition-opacity opacity-0 group-hover:opacity-100"
													>
														<AiOutlineClose className="text-red-500 text-sm" />
													</button>
												</div>
											))}
										</div>
									</div>
								)}
							</div>

							<div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 sticky bottom-0 bg-white pb-4 px-2 sm:px-0 border-t sm:border-t-0 border-gray-100">
								<button
									type="submit"
									disabled={submitting}
									className="w-full sm:w-auto order-1 sm:order-2 px-8 py-3 bg-[#5d4037] hover:bg-[#3e2723] text-[#fdfaf6] rounded-lg font-serif font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md active:scale-[0.98]"
								>
									{submitting ? (
										<span className="flex items-center justify-center gap-2">
											<svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
											</svg>
											Processing...
										</span>
									) : (
										'Submit Review'
									)}
								</button>

								<button
									type="button"
									onClick={onClose}
									className="w-full sm:w-auto order-2 sm:order-1 px-6 py-3 border-2 border-[#eaddcf] rounded-lg text-[#8d6e63] font-serif font-bold hover:bg-[#fdfaf6] hover:border-[#8d6e63] transition-all"
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReviewForm;